// apps/merchant-v3/src/layouts/Sidebar.tsx
//
// Phase 6 Wave 1 Task 02 — Grouped collapsible sidebar.
//
//   Top-level: Dashboard, Orders
//   Group "Products": Catalog, My Products, Artwork Library
//   Group "Stores":   Store Settings, Sync Logs
//   Group "Finance": Wallet, Invoices [+badge], Team
//   Group "Settings": Profile
//   Top-level peers of Settings: Team, Branding, Support
//
// Width: 64px collapsed (icon-only) / 240px expanded. Collapse + group
// open/close persist via Zustand persist middleware to localStorage key
// `merchant-v3.sidebar.state.v1`. Lucide icons per CLAUDE.md.
//
// Phase 6 Wave 5 Task 15 — Repoint the "Artwork Library" group entry
// from `/products/artwork` (legacy alias) to the canonical `/artwork`
// route and use the i18n key `artwork.nav.label` so VI/EN/FR copy
// flows from `common.json`.
//
// Phase 6 Wave 6 Task 19 — Add top-level "Create order" entry pointing
// to `/orders/create` (the Order Create Wizard). Uses PlusCircle icon
// per the task spec; data-testid `sidebar-link-orders-create` so E2E
// can target it.
//
// Phase 6 Wave 6 Task 20 — Add top-level "Excel import" entry pointing
// to `/orders/import` (the new Excel Import wizard). UploadCloud icon
// per task spec; data-testid `sidebar-link-orders-import` for E2E.
//
// Phase 6 Wave 6 Task 21 — Add "Custom views" + "Settings" sub-items
// under the Orders section. data-testids `sidebar-link-orders-views`
// and `sidebar-link-orders-settings` for E2E. Sliders icon for views,
// gear / settings icon for the order settings entry.
//
// Phase 6 Wave 10 Task 32 — Add "Billing" entry under Finance pointing
// to the new `/billing` overview hub (Amendment #1).
// Reuses InvoicesBadge so the count surfaces on the new entry too —
// red dot when overdue_count > 0, yellow when due_soon_count > 0,
// hidden otherwise. data-testid `sidebar-link-billing`.
//
// Phase 6 Wave 12 Task 40 — Add top-level "Notifications" entry pointing
// to the new `/notifications` Notification Center surface. Trailing
// slot renders the new <NotificationsBadge /> component (unread count
// pulled from the React Query cache + WS-live-updated). data-testid
// `sidebar-link-notifications` for E2E..
//
// vg-binding: PLAN:task-24
// vg-binding: P6.1.D-27
// vg-binding: P6.1.D-02
// vg-binding: G-15
// vg-binding: CONTEXT:decisions
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: PLAN:task-02
// vg-binding: PLAN:task-40
// vg-binding: P6.D-06
// vg-binding: P6.D-118
// vg-binding: P6.D-120
// vg-binding: G-65
// vg-binding: G-66
// vg-binding: PLAN:task-15
// vg-binding: PLAN:task-19
// vg-binding: PLAN:task-20
// vg-binding: PLAN:task-21
// vg-binding: PLAN:task-32
// vg-binding: P6.D-01
// vg-binding: P6.D-04
// vg-binding: P6.D-92
// vg-binding: P6.D-245
// vg-binding: P6.D-247
// vg-binding: P6.D-53
// vg-binding: P6.D-62
// vg-binding: P6.D-63
// vg-binding: G-34
// vg-binding: G-35
// vg-binding: G-36
// vg-binding: G-52
// vg-binding: G-53

import { Fragment, type ReactNode, useEffect, useId, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  Truck,
  FolderOpen,
  Store,
  Wallet,
  Settings,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Box,
  Image as ImageIcon,
  Users,
  UsersRound,
  Palette,
  LifeBuoy,
  UserCircle,
  Layers,
  type LucideIcon,
} from 'lucide-react';

import { useSidebarStore, type SidebarGroupKey } from '@/store/sidebar';
import {
  resolveActiveNavTo,
  type ActiveNavCandidate,
} from '@/lib/nav/activeNav';
import { cn } from '@/lib/cn';
import { sidebarNavItemActiveClassName } from '@printway/ui';
import { useTranslation } from 'react-i18next';
import { MerchantSupportBadge } from '@/features/support/supportRealtime';
// Phase 6.1 Wave 5 Task 24 — hide the Members / Teams settings entries for
// staff/viewer (no member:read / team:read). Complements the /403 route
// guard so those roles never see a link they cannot open (D-27, G-15).
import { usePermissions } from '@/lib/scope/usePermissions';

interface NavItem {
  to: string;
  /** i18n key resolved via `t('common', labelKey)` at render time. */
  labelKey: string;
  icon: LucideIcon;
  /** data-testid for E2E selectors (test_ids declared in plan task-02). */
  testId?: string;
  /** Optional trailing slot (e.g., InvoicesBadge). */
  trailing?: ReactNode;
  /** When true, hide this item entirely (env feature flag). */
  hidden?: boolean;
  /** Phase 6.1 Task 24 — when true, hide for staff/viewer (no member/team
   *  read access). Resolved dynamically in <Sidebar> via usePermissions. */
  settingsGated?: boolean;
  /** Extra path prefixes this row stays active for, on top of NavLink's
   *  exact match (`end`). Narrow opt-in so B10's single-active rule holds:
   *  list only routes that have NO sidebar row of their own (e.g. the
   *  standalone /orders/create wizard route → the Orders row, G-30). */
  alsoActiveOn?: string[];
}

interface NavGroup {
  key: SidebarGroupKey;
  labelKey: string;
  icon: LucideIcon;
  testId?: string;
  items: NavItem[];
}

// --- helpers --------------------------------------------------------------

// 2026-05-22 — i18n cleanup: all sidebar labels resolve via `t()` against
// `navigation.*` keys in common.json. Previously labels were hardcoded
// (mix of English literals + 3 Vietnamese leaks "Đội ngũ" / "Thông báo" /
// "Lịch sử đăng nhập") which surfaced VN strings when locale=EN.

// --- internal pieces ------------------------------------------------------

function SideNavLink({
  to,
  labelKey,
  icon: Icon,
  testId,
  trailing,
  collapsed,
  indent = false,
  active,
}: NavItem & {
  collapsed: boolean;
  indent?: boolean;
  /** Decided by <Sidebar> via resolveActiveNavTo — see dbg-20260911-080936. */
  active: boolean;
}): JSX.Element {
  const { t } = useTranslation('common');
  const label = t(labelKey);
  return (
    <NavLink
      to={to}
      // dbg-20260911-080936 — the active decision is NOT NavLink's any more.
      //
      // It used to be `end` (exact) with a per-route opt-out, which left 31 of
      // 41 measured routes lighting nothing at all: /products/my, every
      // /wallet/*, /billing/*, /settings/* bar one. NavLink can only offer
      // exact-or-any-prefix, and neither is the rule this sidebar needs —
      // "most specific row wins" requires comparing rows against each other,
      // which a single link cannot do from inside itself.
      //
      // `end` stays only to keep RR7 from ALSO applying its own prefix match
      // (and its aria-current) on top of ours; the styling and the
      // aria-current below are driven by `active`.
      end
      aria-current={active ? 'page' : undefined}
      data-testid={testId}
      data-active={active ? 'true' : 'false'}
      title={collapsed ? label : undefined}
      className={
        cn(
          // V3 replica (docs/images/v3): pill rows — h-12, 16px radius,
          // 16px horizontal padding. The active row uses the shared
          // @printway/ui style (solid orange, white text/icon — owner
          // decision 2026-10-30); the row keeps its 16px pill radius.
          'group flex h-12 items-center gap-3 rounded-2xl px-4 text-[15px] font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
          collapsed ? 'justify-center px-0' : indent ? 'pl-8 pr-3' : 'px-4',
          active
            ? sidebarNavItemActiveClassName
            : 'text-pw-text-muted hover:bg-pw-surface hover:text-pw-text',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      {!collapsed ? (
        <>
          <span className="flex-1 truncate">{label}</span>
          {trailing}
        </>
      ) : null}
    </NavLink>
  );
}

function SideGroup({
  group,
  collapsed,
  activeTo,
}: {
  group: NavGroup;
  collapsed: boolean;
  /** `to` of the one row that owns the highlight right now, if any. */
  activeTo: string | null;
}): JSX.Element {
  const { t } = useTranslation('common');
  const storeOpen = useSidebarStore((s) => s.groupsOpen[group.key]);
  const toggleGroup = useSidebarStore((s) => s.toggleGroup);
  const setGroupOpen = useSidebarStore((s) => s.setGroupOpen);
  const headerId = useId();
  const panelId = useId();
  const Icon = group.icon;
  const groupLabel = t(group.labelKey);
  const visibleItems = group.items.filter((i) => !i.hidden);

  const holdsActive =
    activeTo !== null && visibleItems.some((i) => i.to === activeTo);

  // dbg-20260911-080936 — a group holding the current page opens itself.
  //
  // Every group defaults to CLOSED and nothing ever opened them, so the rows
  // inside were not rendered at all — which is why /products/my lit nothing
  // even though it has a row of its own. Measured: with the group shut the
  // sidebar rendered no /products/* link whatsoever; opening it by hand
  // immediately showed `/products/my` lit. So this was never only a matching
  // bug — half of it was the row not existing in the DOM.
  //
  // Written to the STORE rather than held in local state, because the store's
  // click-toggle is an accordion (opening one closes the rest): a local
  // override would fight it and the operator's next click would appear to do
  // nothing. Writing through means arriving on a page and clicking a group end
  // up in the same place.
  //
  // Keyed on the ROUTE, not on `holdsActive` alone. An earlier pass used
  // `open = storeOpen || holdsActive`, and measuring caught it: collapsing the
  // group you were currently inside did nothing at all, because `holdsActive`
  // forced it back open on the same render. Opening happens once per arrival;
  // after that the group is the operator's to close.
  const openedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!holdsActive) {
      openedForRef.current = null;
      return;
    }
    if (openedForRef.current === activeTo) return;
    openedForRef.current = activeTo;
    setGroupOpen(group.key, true);
  }, [holdsActive, activeTo, setGroupOpen, group.key]);

  const open = storeOpen;

  // When collapsed: render flat icon list (no group header) — group label
  // is replaced by the item icons themselves with tooltip on hover.
  if (collapsed) {
    return (
      <div role="group" aria-label={groupLabel} className="flex flex-col gap-0.5">
        {visibleItems.map((item) => (
          <SideNavLink
            key={item.to}
            {...item}
            collapsed
            active={item.to === activeTo}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        id={headerId}
        data-testid={group.testId}
        aria-expanded={open}
        aria-controls={panelId}
        // Marks the group whose page you are on, so the collapsed-but-current
        // case is still legible at a glance.
        data-holds-active={holdsActive ? 'true' : 'false'}
        onClick={() => toggleGroup(group.key)}
        className={cn(
          'group flex h-12 items-center gap-3 rounded-2xl px-4 text-[15px] font-medium transition-colors hover:bg-pw-surface hover:text-pw-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
          holdsActive ? 'text-pw-text' : 'text-pw-text-muted',
        )}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden />
        <span className="flex-1 truncate text-left">{groupLabel}</span>
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
        )}
      </button>
      {open ? (
        <div
          id={panelId}
          role="region"
          aria-labelledby={headerId}
          className="flex flex-col gap-0.5"
        >
          {visibleItems.map((item) => (
            <SideNavLink
              key={item.to}
              {...item}
              collapsed={false}
              indent
              active={item.to === activeTo}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

// --- exported Sidebar -----------------------------------------------------

const TOP_LEVEL: NavItem[] = [
  {
    to: '/dashboard',
    labelKey: 'navigation.dashboard',
    icon: LayoutDashboard,
    testId: 'sidebar-link-dashboard',
  },
  {
    to: '/orders',
    labelKey: 'navigation.orders',
    icon: ShoppingCart,
    testId: 'sidebar-link-orders',
    // G-30 — /orders/create and friends now fall out of the prefix rule, so
    // no declaration is needed here any more. Kept for `/orders/views` etc.
    // only if they ever move off the /orders prefix.
  },
  {
    to: '/shipments',
    labelKey: 'navigation.shipments',
    icon: Truck,
    testId: 'sidebar-link-shipments',
  },
];

// 2026-06-27 (dbg) — Stores group dissolved per merchant UX request.
// "Store" is now a single top-level link → /stores (the former
// "Store Settings" sub-item, now the group's only destination). The
// "Sync Logs" sub-item was dropped along with the group. Renders right
// above Wallet, where the Stores group used to sit.
const STORE_ITEM: NavItem = {
  to: '/stores',
  labelKey: 'navigation.stores',
  icon: Store,
  testId: 'sidebar-link-stores',
  // `/sync-logs` is the canonical cross-store path (P6.D-223); the
  // `/stores/sync-logs` alias is covered by the prefix rule already.
  alsoActiveOn: ['/sync-logs'],
};

// 2026-06-26 (dbg) — Finance group dissolved per merchant UX request.
// "Wallet" is now a single top-level link → /wallet (Billing + Invoices
// moved into in-page tabs on the Wallet overview page). "Team" keeps a
// dedicated single link → /team. Both render between the Stores group
// and the Settings group, above Notifications.
const WALLET_ITEM: NavItem = {
  to: '/wallet',
  labelKey: 'navigation.wallet',
  icon: Wallet,
  testId: 'sidebar-link-wallet',
  // Billing lives in this page's tabs (see the 2026-06-26 note above) and
  // shares no path prefix with it, so the ownership has to be stated.
  alsoActiveOn: ['/billing'],
};

// 2026-06-27 — Notifications top-level entry dropped. Notifications now
// live as an in-page tab on /settings/profile (see commit 77c63bb41).
const GROUPS: NavGroup[] = [
  {
    key: 'products',
    labelKey: 'navigation.products',
    icon: FolderOpen,
    testId: 'sidebar-group-products',
    items: [
      {
        to: '/products',
        labelKey: 'navigation.sidebar.catalog',
        icon: Layers,
        testId: 'sidebar-link-catalog',
      },
      {
        to: '/products/my',
        labelKey: 'navigation.sidebar.myProducts',
        icon: Box,
        testId: 'sidebar-link-my-products',
        // dbg-20260911-080936 — the reported bug. "Add new product" is reached
        // from My Products, but /products/create sits under the /products
        // prefix, so the prefix rule alone would hand it to Catalog. Ownership
        // here is a fact about the flow, not about the URL shape.
        //
        // Same for the other two: wizard-drafts is this creation flow resumed,
        // and /products/settings is opened from the My Products page itself
        // (MyProducts.tsx:713) — measuring first showed both landing on
        // Catalog, which is not where either is reached from.
        alsoActiveOn: [
          '/products/create',
          '/products/wizard-drafts',
          '/products/settings',
        ],
      },
      {
        to: '/artwork',
        labelKey: 'artwork.nav.label',
        icon: ImageIcon,
        testId: 'sidebar-link-artwork',
        // `/products/artwork` renders the SAME ArtworkLibraryPage component
        // (router.tsx:30) — one surface reachable at two paths, so both must
        // light this row rather than the Catalog row the prefix would pick.
        alsoActiveOn: ['/products/artwork'],
      },
    ],
  },
  {
    key: 'settings',
    labelKey: 'navigation.settings',
    icon: Settings,
    testId: 'sidebar-group-settings',
    items: [
      {
        to: '/settings/profile',
        labelKey: 'navigation.sidebar.profile',
        icon: UserCircle,
        testId: 'sidebar-link-settings-profile',
        // This row stands for the whole Settings surface, whose other tabs are
        // separate routes: SettingsTabs.tsx:39-46 maps Preferences →
        // /settings/general, Activity → /settings/login-history, API keys →
        // /settings/api-keys, Security → /settings/security. Notifications
        // moved into this page's tabs too (2026-06-27 note below). None of
        // them share the /settings/profile prefix, so each is declared.
        alsoActiveOn: [
          '/notifications',
          '/settings/security',
          '/settings/general',
          '/settings/api-keys',
          '/settings/login-history',
          '/settings/sessions',
        ],
      },
      // 2026-07-13 — Team moved back INTO the Settings submenu (user request):
      // it lives under Settings, so it belongs as a Settings sub-item rather
      // than a top-level peer.
      //
      // Phase 6.1 Wave 5 Task 21 (P6.1.D-26) — the single "Team Members"
      // entry is split into two: "Members" (→ /settings/member, the renamed
      // members page) and "Teams" (→ /settings/teams, the new store-based
      // teams page owned by task-23). Old `/settings/team` permanently
      // redirects to /settings/member (router.tsx).
      {
        to: '/settings/member',
        labelKey: 'navigation.members',
        icon: Users,
        testId: 'sidebar-link-members',
        // Task 24 — hidden for staff/viewer (lack member:read).
        settingsGated: true,
      },
      {
        to: '/settings/teams',
        labelKey: 'navigation.teams',
        icon: UsersRound,
        testId: 'sidebar-link-teams',
        // Task 24 — hidden for staff/viewer (lack team:read).
        settingsGated: true,
      },
      // Branding + Support remain top-level peers of Settings
      // (BRANDING_ITEM / SUPPORT_ITEM below).
    ],
  },
];

// 2026-06-26 — Branding promoted out of the Settings submenu to a top-level
// item rendered directly after the Settings group. (Team was moved back into
// the Settings submenu 2026-07-13 — see GROUPS.)
const BRANDING_ITEM: NavItem = {
  to: '/settings/branding',
  labelKey: 'navigation.branding',
  icon: Palette,
  testId: 'sidebar-link-branding',
};

// 2026-07-04 — Support module (ticket + inbox) shipped; promoted out of the
// Settings group to a top-level item, peer of Settings/Team/Branding, matching
// vendor which surfaces Support as a top-level link. Previously hidden behind
// the VITE_SUPPORT_ENABLED rollout gate; that flag is still read independently
// by the account-suspension UI (SuspendOverlay/SuspendBanner) and is untouched.
const SUPPORT_ITEM: NavItem = {
  to: '/tickets',
  labelKey: 'navigation.tickets',
  icon: LifeBuoy,
  testId: 'sidebar-link-support',
  // `/support` and `/support/tickets` redirect here but are distinct paths.
  alsoActiveOn: ['/support'],
};

export interface SidebarProps {
  /**
   * 'fixed' (default) — the always-mounted desktop aside, hidden below lg
   * where the off-canvas drawer takes over (AppShell renders a second
   * <Sidebar variant="drawer" /> inside a Sheet there).
   * 'drawer' — fills the Sheet panel, always expanded, no collapse toggle.
   * Replaces the dbg-20260703 icon-rail-on-mobile behavior: the rail still
   * ate 64px of phone width and had no full-label navigation.
   */
  variant?: 'fixed' | 'drawer';
}

export function Sidebar({ variant = 'fixed' }: SidebarProps): JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const storeCollapsed = useSidebarStore((s) => s.collapsed);
  const toggleCollapsed = useSidebarStore((s) => s.toggleCollapsed);
  const inDrawer = variant === 'drawer';
  // Drawer always renders expanded; fixed follows the persisted preference.
  const collapsed = inDrawer ? false : storeCollapsed;

  // Phase 6.1 Task 24 — role-gate the Members / Teams settings entries.
  // staff/viewer lack member:read / team:read → hide the links entirely so
  // they never see a nav item that 403s on click (D-27, G-15). While perms
  // load we keep them visible (optimistic) — the /403 route guard is the
  // hard enforcement layer, so a brief flash never leaks data.
  const { canReadMembers, canReadTeams, isLoading: permsLoading } =
    usePermissions();
  const groups = GROUPS.map((group) => {
    if (group.key !== 'settings') return group;
    return {
      ...group,
      items: group.items.map((item) => {
        if (!item.settingsGated) return item;
        const allowed = permsLoading
          ? true
          : item.to === '/settings/teams'
            ? canReadTeams
            : canReadMembers;
        return allowed ? item : { ...item, hidden: true };
      }),
    };
  });

  // dbg-20260911-080936 — ONE decision for the whole sidebar.
  //
  // Every row is scored against the current path and the most specific one
  // wins (see lib/nav/activeNav.ts). Doing it here rather than inside each
  // link is what makes "most specific" possible at all: a link cannot know
  // whether a longer-matching sibling exists. `groups` is used, not `GROUPS`,
  // so a row hidden by permissions cannot win the highlight.
  const { pathname } = useLocation();
  const allRows: ActiveNavCandidate[] = [
    ...TOP_LEVEL,
    ...groups.flatMap((g) => g.items),
    STORE_ITEM,
    WALLET_ITEM,
    BRANDING_ITEM,
    SUPPORT_ITEM,
  ];
  const activeTo = resolveActiveNavTo(pathname, allRows);

  return (
    <aside
      aria-label={tCommon('a11y.primaryNavigation')}
      data-collapsed={collapsed}
      data-active-nav={activeTo ?? 'none'}
      className={cn(
        'h-full shrink-0 flex-col border-pw-border bg-pw-card transition-[width] duration-150',
        inDrawer
          ? 'flex w-full'
          : cn('hidden border-r lg:flex', collapsed ? 'w-16' : 'w-[240px]'),
      )}
    >
      {/* Brand + collapse toggle (dzung-order Image #7 — printway.io
          brand mark replaces the previous "Printway Merchant" wordmark). */}
      <div className="flex h-14 items-center gap-2 border-b border-pw-border px-3">
        <img
          src="/printway-logo.avif"
          alt="Printway"
          className="h-8 w-auto shrink-0"
        />
        {!collapsed ? <span className="flex-1" /> : null}
        {/* Collapse toggle — desktop-only concern: the fixed aside is hidden
            below lg and the drawer variant is always expanded. */}
        {!inDrawer ? (
          <button
            type="button"
            data-testid="sidebar-collapse-toggle"
            aria-label={
              collapsed
                ? tCommon('a11y.expandSidebar', { defaultValue: 'Expand sidebar' })
                : tCommon('a11y.collapseSidebar', { defaultValue: 'Collapse sidebar' })
            }
            aria-expanded={!collapsed}
            onClick={toggleCollapsed}
            className={cn(
              'inline-flex h-7 w-7 items-center justify-center rounded text-pw-text-muted hover:bg-pw-surface hover:text-pw-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              collapsed && 'mx-auto',
            )}
          >
            <ChevronLeft
              className={cn(
                'h-4 w-4 transition-transform',
                collapsed && 'rotate-180',
              )}
              aria-hidden
            />
          </button>
        ) : null}
      </div>

      {/* Nav body */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <div className="flex flex-col gap-0.5">
          {TOP_LEVEL.map((item) => (
            <SideNavLink
              key={item.to}
              {...item}
              collapsed={collapsed}
              active={item.to === activeTo}
            />
          ))}
        </div>

        <div className="mt-2 flex flex-col gap-0.5">
          {groups.map((group) => (
            <Fragment key={group.key}>
              <SideGroup
                group={group}
                collapsed={collapsed}
                activeTo={activeTo}
              />
              {group.key === 'products' ? (
                <>
                  <SideNavLink
                    {...STORE_ITEM}
                    collapsed={collapsed}
                    active={STORE_ITEM.to === activeTo}
                  />
                  <SideNavLink
                    {...WALLET_ITEM}
                    collapsed={collapsed}
                    active={WALLET_ITEM.to === activeTo}
                  />
                </>
              ) : null}
              {group.key === 'settings' ? (
                <>
                  {/* Team now renders as a Settings sub-item (see GROUPS). */}
                  <SideNavLink
                    {...BRANDING_ITEM}
                    collapsed={collapsed}
                    active={BRANDING_ITEM.to === activeTo}
                  />
                  <SideNavLink
                    {...SUPPORT_ITEM}
                    trailing={<MerchantSupportBadge />}
                    collapsed={collapsed}
                    active={SUPPORT_ITEM.to === activeTo}
                  />
                </>
              ) : null}
            </Fragment>
          ))}
        </div>
      </nav>
    </aside>
  );
}

export default Sidebar;
